import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { CoinIcon, Pct, Spark, fmtPrice } from "@/components/layernav/primitives";
import { COIN_META } from "@/components/layernav/navConfig";
import { RailModule } from "./RailModules";

const NAME_TO_SYM: Record<string, string> = {
  bitcoin: "BTC", btc: "BTC", ethereum: "ETH", eth: "ETH", solana: "SOL", sol: "SOL",
  xrp: "XRP", dogecoin: "DOGE", doge: "DOGE", cardano: "ADA", ada: "ADA",
};
const TITLE_RE = /\b(bitcoin|ethereum|solana|xrp|dogecoin|cardano)\b/gi;

export const detectAssets = (article: Article): string[] => {
  const out: string[] = [];
  const add = (s?: string) => { if (s && !out.includes(s)) out.push(s); };
  for (const tok of [...(article.entities || []), ...(article.tags || [])]) {
    add(NAME_TO_SYM[tok.toLowerCase()]);
  }
  for (const m of (article.title || "").matchAll(TITLE_RE)) {
    add(NAME_TO_SYM[m[0].toLowerCase()]);
  }
  return out.slice(0, 2);
};

const RANGES = ["24h", "7d", "30d"] as const;

export const MarketContext = ({ article }: { article: Article }) => {
  const backendSyms = (article.context?.assets_detected || []).slice(0, 2);
  const syms = backendSyms.length ? backendSyms : detectAssets(article);
  const primary = syms[0];
  const [range, setRange] = useState<(typeof RANGES)[number]>("24h");
  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    staleTime: 90 * 1000,
    enabled: !!primary,
  });
  const { data: chartData } = useQuery({
    queryKey: ["asset-chart", primary, range],
    queryFn: async () => (await api.get(`/markets/${primary}?range=${range}`)).data,
    staleTime: 5 * 60 * 1000,
    enabled: !!primary,
  });

  if (!primary) return null;
  const coin = (markets?.coins || []).find((c: any) => c.symbol === primary);
  if (!coin) return null;

  const chart: number[] = (chartData?.chart || []).map((p: any) => p.price);
  const rangeDelta = range === "24h"
    ? coin.change_24h
    : chart.length > 1 && chart[0] > 0 ? ((chart[chart.length - 1] - chart[0]) / chart[0]) * 100 : null;
  const secondary = syms[1] ? (markets?.coins || []).find((c: any) => c.symbol === syms[1]) : null;

  return (
    <RailModule label="Market Context" cyan testid="market-context" track="context_market">
      <div className="flex items-center gap-2.5">
        <CoinIcon symbol={primary} size={26} />
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-white leading-tight">{COIN_META[primary]?.name || primary}</p>
          <p className="font-mono text-[9px] uppercase text-gray-600">{primary}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-mono text-[17px] text-white leading-tight" data-testid="market-context-price">{fmtPrice(coin.price)}</p>
          {rangeDelta !== null && (
            <p className="font-mono text-[10px]">
              <Pct v={rangeDelta} /> <span className="text-gray-600 uppercase">{range}</span>
            </p>
          )}
        </div>
      </div>
      {chart.length > 1 && (
        <div className="mt-3">
          <Spark points={chart} up={(rangeDelta ?? 0) >= 0} className="h-12 w-full" />
        </div>
      )}
      <div className="mt-3 grid grid-cols-3 gap-1 rounded-lg border border-white/[0.08] p-1" role="tablist" aria-label="Chart range">
        {RANGES.map((r) => (
          <button key={r} type="button" role="tab" aria-selected={range === r} onClick={() => setRange(r)}
            data-testid={`market-range-${r}`}
            className={`rounded-md py-1 font-mono text-[10px] font-bold uppercase transition-colors ${
              range === r ? "bg-white/[0.08] text-white" : "text-gray-500 hover:text-gray-300"}`}>
            {r}
          </button>
        ))}
      </div>
      {secondary && (
        <div className="mt-3 border-t border-white/[0.06] pt-3 flex items-center gap-2">
          <CoinIcon symbol={secondary.symbol} size={20} />
          <span className="font-mono text-xs font-bold text-gray-300">{secondary.symbol}</span>
          <span className="ml-auto font-mono text-[13px] text-white">{fmtPrice(secondary.price)}</span>
          <Pct v={secondary.change_24h} className="text-[10px]" />
        </div>
      )}
    </RailModule>
  );
};
